import React, { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import PasskeySetup from '../PasskeySetup';
import LinkedSignInOptions from '../LinkedSignInOptions';
import { Bell, Car, User, MapPin, CheckCircle, Mail, Smartphone } from 'lucide-react';

const UrbanrideNotificationSettings = () => {
  const [preferences, setPreferences] = useState({
    chauffeur_assigned: true,
    vehicle_on_location: true,
    passenger_on_board: true,
    ride_completed: true,
    status_changes: true,
    email_notifications: false,
    push_notifications: true
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadPreferences();
  }, []);

  const loadPreferences = async () => {
    try {
      const response = await axios.get('/users/me/urbanride-notification-preferences');
      setPreferences(response.data);
    } catch (error) {
      console.error('Failed to load notification preferences:', error);
    } finally {
      setLoading(false);
    }
  };

  const updatePreference = async (key, value) => {
    setSaving(true);
    try {
      const newPrefs = { ...preferences, [key]: value };
      setPreferences(newPrefs);
      await axios.put('/users/me/urbanride-notification-preferences', { [key]: value });
    } catch (error) {
      console.error('Failed to update preference:', error);
      loadPreferences(); // Revert on error
    } finally {
      setSaving(false);
    }
  };

  const notificationOptions = [
    { key: 'chauffeur_assigned', label: 'Chauffeur Assigned', icon: User, description: 'When a driver is assigned to your ride' },
    { key: 'vehicle_on_location', label: 'Vehicle on Location', icon: MapPin, description: 'When your driver arrives at pickup' },
    { key: 'passenger_on_board', label: 'Passenger on Board', icon: Car, description: 'When the ride begins' },
    { key: 'ride_completed', label: 'Ride Completed', icon: CheckCircle, description: 'When the ride is finished' },
    { key: 'status_changes', label: 'Status Changes', icon: Bell, description: 'Booking confirmations, modifications, cancellations' },
  ];

  if (loading) {
    return <div className="animate-pulse h-48 bg-slate-100 rounded-lg" />;
  }

  return (
    <div>
      <h3 className="text-base sm:text-lg font-semibold text-slate-800 mb-4">Chauffeur Service Notifications</h3>
      <p className="text-sm text-slate-600 mb-4">
        Choose which updates you want to receive for your Urbanride chauffeur services.
      </p>

      <div className="space-y-3">
        {notificationOptions.map(({ key, label, icon: Icon, description }) => (
          <label key={key} className="flex items-start gap-3 cursor-pointer group p-2 rounded-lg hover:bg-slate-50">
            <input
              type="checkbox"
              checked={preferences[key]}
              onChange={(e) => updatePreference(key, e.target.checked)}
              className="mt-1 w-5 h-5 sm:w-4 sm:h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
            />
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <Icon className="w-4 h-4 text-slate-400 group-hover:text-slate-600" />
                <span className="font-medium text-slate-700">{label}</span>
              </div>
              <span className="text-sm text-slate-500">{description}</span>
            </div>
          </label>
        ))}
      </div>

      <div className="mt-4 pt-4 border-t border-slate-200">
        <h4 className="text-sm font-medium text-slate-700 mb-3">Notification Channels</h4>
        <div className="space-y-3">
          <label className="flex items-center gap-3 cursor-pointer p-2 rounded-lg hover:bg-slate-50">
            <input
              type="checkbox"
              checked={preferences.push_notifications}
              onChange={(e) => updatePreference('push_notifications', e.target.checked)}
              className="w-5 h-5 sm:w-4 sm:h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
            />
            <Smartphone className="w-4 h-4 text-slate-400" />
            <span className="text-slate-700">In-app notifications</span>
          </label>
          <label className="flex items-center gap-3 cursor-pointer p-2 rounded-lg hover:bg-slate-50">
            <input
              type="checkbox"
              checked={preferences.email_notifications}
              onChange={(e) => updatePreference('email_notifications', e.target.checked)}
              className="w-5 h-5 sm:w-4 sm:h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
            />
            <Mail className="w-4 h-4 text-slate-400" />
            <span className="text-slate-700">Email notifications</span>
          </label>
        </div>
      </div>

      {saving && (
        <div className="mt-3 text-sm text-blue-600 flex items-center gap-2">
          <div className="animate-spin w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full" />
          Saving...
        </div>
      )}
    </div>
  );
};

const Profile = () => {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showPasskeySetup, setShowPasskeySetup] = useState(false);

  const [dateInputFormat, setDateInputFormat] = useState('US');
  const [timeViewFormat, setTimeViewFormat] = useState('AMPM');

  useEffect(() => {
    const prefs = user?.ui_preferences || {};
    setDateInputFormat(prefs.date_input_format || 'US');
    setTimeViewFormat(prefs.time_view_format || 'AMPM');
  }, [user]);

  const refreshPrefs = async () => {
    try {
      setLoading(true);
      const res = await axios.get('/users/me/preferences');
      const prefs = res.data || {};
      setDateInputFormat(prefs.date_input_format || 'US');
      setTimeViewFormat(prefs.time_view_format || 'AMPM');
      // update context user copy
      if (user) {
        setUser({ ...user, ui_preferences: prefs });
      }
    } catch (e) {
      console.warn('Failed to refresh preferences', e);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setError('');
    setSuccess('');
    setSaving(true);
    try {
      const payload = { date_input_format: dateInputFormat, time_view_format: timeViewFormat };
      const res = await axios.put('/users/me/preferences', payload);
      const prefs = res.data || payload;
      setSuccess('Preferences saved');
      if (user) {
        setUser({ ...user, ui_preferences: prefs });
      }
    } catch (e) {
      console.error(e);
      setError(e?.response?.data?.detail || 'Failed to save preferences');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <h2 className="text-xl sm:text-2xl font-semibold text-slate-800">Profile &amp; Preferences</h2>
        <div className="flex items-center gap-2 sm:gap-3">
          <Link to="/" className="flex-1 sm:flex-none px-3 py-2 text-sm border border-slate-300 rounded-lg hover:bg-slate-50 text-center touch-target">Dashboard</Link>
          <button
            onClick={() => navigate(-1)}
            className="flex-1 sm:flex-none px-3 py-2 text-sm border border-slate-300 rounded-lg hover:bg-slate-50 touch-target"
            title="Go Back"
          >
            Back
          </button>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-4 sm:p-6 space-y-6">
        <div>
          <div className="text-sm text-slate-500 mb-1">Signed in as</div>
          <div className="text-slate-800 font-medium break-all">{user?.email}</div>
        </div>

        <div className="border-t border-slate-200 pt-6">
          <h3 className="text-base sm:text-lg font-semibold text-slate-800 mb-4">Display &amp; Input Preferences</h3>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
            <div>
              <div className="text-sm font-medium text-slate-700 mb-2">Date input format</div>
              <div className="space-y-3 sm:space-y-2">
                <label className="flex items-center gap-3 sm:gap-2 p-2 sm:p-0 rounded-lg hover:bg-slate-50 sm:hover:bg-transparent cursor-pointer touch-target">
                  <input
                    type="radio"
                    name="dateInputFormat"
                    value="US"
                    checked={dateInputFormat === 'US'}
                    onChange={() => setDateInputFormat('US')}
                    className="w-5 h-5 sm:w-4 sm:h-4"
                  />
                  <span>US (MM/DD/YYYY)</span>
                </label>
                <label className="flex items-center gap-3 sm:gap-2 p-2 sm:p-0 rounded-lg hover:bg-slate-50 sm:hover:bg-transparent cursor-pointer touch-target">
                  <input
                    type="radio"
                    name="dateInputFormat"
                    value="EU"
                    checked={dateInputFormat === 'EU'}
                    onChange={() => setDateInputFormat('EU')}
                    className="w-5 h-5 sm:w-4 sm:h-4"
                  />
                  <span>EU (DD/MM/YYYY)</span>
                </label>
              </div>
            </div>

            <div>
              <div className="text-sm font-medium text-slate-700 mb-2">Time view format</div>
              <div className="space-y-3 sm:space-y-2">
                <label className="flex items-center gap-3 sm:gap-2 p-2 sm:p-0 rounded-lg hover:bg-slate-50 sm:hover:bg-transparent cursor-pointer touch-target">
                  <input
                    type="radio"
                    name="timeViewFormat"
                    value="AMPM"
                    checked={timeViewFormat === 'AMPM'}
                    onChange={() => setTimeViewFormat('AMPM')}
                    className="w-5 h-5 sm:w-4 sm:h-4"
                  />
                  <span>AM/PM</span>
                </label>
                <label className="flex items-center gap-3 sm:gap-2 p-2 sm:p-0 rounded-lg hover:bg-slate-50 sm:hover:bg-transparent cursor-pointer touch-target">
                  <input
                    type="radio"
                    name="timeViewFormat"
                    value="24H"
                    checked={timeViewFormat === '24H'}
                    onChange={() => setTimeViewFormat('24H')}
                    className="w-5 h-5 sm:w-4 sm:h-4"
                  />
                  <span>24 Hour</span>
                </label>
              </div>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center gap-3 mt-6">
            <button
              className="w-full sm:w-auto px-4 py-2.5 sm:py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 touch-target"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? 'Saving...' : 'Save Preferences'}
            </button>
            <button
              className="w-full sm:w-auto px-4 py-2.5 sm:py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 touch-target"
              onClick={refreshPrefs}
              disabled={loading}
            >
              {loading ? 'Refreshing...' : 'Refresh'}
            </button>
            {success && <div className="text-green-600 text-sm text-center sm:text-left">{success}</div>}
            {error && <div className="text-red-600 text-sm text-center sm:text-left">{error}</div>}
          </div>
        </div>

        <div className="border-t border-slate-200 pt-6">
          <h3 className="text-base sm:text-lg font-semibold text-slate-800 mb-4">Security & Authentication</h3>
          <div className="mb-6"><LinkedSignInOptions organizationId={user?.organization_id} /></div>
          <div className="space-y-4">
            <div>
              <h4 className="text-sm font-medium text-slate-700 mb-2">Passkeys</h4>
              <p className="text-sm text-slate-600 mb-3">
                Passkeys let you sign in using your fingerprint, face, or screen lock.
                They're more secure than passwords and work across your devices.
              </p>
              <button
                className="w-full sm:w-auto px-4 py-2.5 sm:py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 touch-target"
                onClick={() => setShowPasskeySetup(!showPasskeySetup)}
              >
                {showPasskeySetup ? 'Hide Passkey Setup' : 'Manage Passkeys'}
              </button>
            </div>
            {showPasskeySetup && (
              <div className="mt-4">
                <PasskeySetup onClose={() => setShowPasskeySetup(false)} />
              </div>
            )}
          </div>
        </div>

        <div className="border-t border-slate-200 pt-6">
          <UrbanrideNotificationSettings />
        </div>
      </div>
    </div>
  );
};

export default Profile;
